import * as d3 from "d3";

export const variables = {
  pct_with_loan: {
    breaks: [0, 0.33, 0.4, 0.46, 0.54, 0.64, 2.25],
    scale: "%",
    multiplier: 100,
    mapbox: [
      "case",
      ["==", ["get", "n_restaurants"], 0],
      -1,
      ["/", ["get", "n_loans"], ["get", "n_restaurants"]],
    ],
    display: "% restaurants with loan",
    js: (v) => v.n_loans / v.n_restaurants,
    description:
      "The number of loan applications divided by the number of restaurants in a particular ZIP Code.",
  },
  loan_per_company: {
    breaks: [0, 12000, 24000, 38000, 55000, 91000, 200000],
    scale: "$",
    multiplier: 1,
    mapbox: [
      "case",
      ["==", ["get", "n_restaurants"], 0],
      -1,
      ["/", ["get", "loan_amount"], ["get", "n_restaurants"]],
    ],
    display: "Loan amount per restaurant",
    js: (v) => v.loan_amount / v.n_restaurants,
    description: "The total loan amount applied for by all restaurants divided by the number of restaurants in a particular ZIP code."
  },
  hispanic_pct: {
    breaks: [0, 0.08, 0.15, 0.25, 0.4, 0.6, 1].map((v) => v * 100),
    scale: "%",
    multiplier: 1,
    mapbox: ["get", "pct_hispanic"],
    color: (v) => d3.interpolateCividis(0.9 - ((v - 0.3) * 1) / 0.8),
    display: "% Hispanic",
    js: (v) => v.pct_hispanic,
  },
  black_and_hispanic: {
    breaks: [0, 0.15, 0.3, 0.45, 0.6, 0.75, 1].map((v) => v * 100),
    scale: "%",
    multiplier: 1,
    mapbox: ["+", ["get", "pct_hispanic"], ["get", "pct_black"]],
    color: (v) => d3.interpolateCividis(0.9 - ((v - 0.3) * 1) / 0.8),
    display: "% Black and Hispanic",
    js: (v) => v.pct_hispanic + v.pct_black,
    filter: ["has", "pct_black"],
  },
  med_income: {
    breaks: [0, 50000, 60000, 70000, 80000, 110000, 250000],
    scale: "$",
    multiplier: 1,
    mapbox: ["get", "med_income"],
    color: (v) => d3.interpolateCividis(((v - 0.3) * 1) / 0.8),
    display: "Median income",
    js: (v) => v.med_income,
    filter: ["has", "med_income"],
    description: "Median household income."
  },
  asian_pct: {
    breaks: [0, 0.01, 0.03, 0.06, 0.1, 0.2, 0.75].map((v) => v * 100),
    scale: "%",
    multiplier: 1,
    mapbox: ["get", "pct_asian"],
    color: (v) => d3.interpolateCividis(0.9 - ((v - 0.3) * 1) / 0.8),
    display: "% Asian",
    js: (v) => v.pct_asian,
  },
  black_pct: {
    breaks: [0, 0.01, 0.03, 0.05, 0.3, 0.6, 0.85].map((v) => v * 100),
    scale: "%",
    multiplier: 1,
    mapbox: ["get", "pct_black"],
    color: (v) => d3.interpolateCividis(0.9 - ((v - 0.3) * 1) / 0.8),
    display: "% Black",
    js: (v) => v.pct_black,
    filter: ["has", "pct_black"],
  },
  loan_total: {
    breaks: [0, 200000, 6e5, 1.4e6, 2.6e6, 5.6e6, 40.2e6],
    scale: "$",
    multiplier: 1,
    mapbox: ["get", "loan_amount"],
    display: "Total loan amount",
    js: (v) => v.loan_amount,
    description: "The total loan amount applied for by all restaurants in a ZIP Code."
  },
  loan_cnt: {
    breaks: [1, 3, 8, 15, 25, 40, 200],
    scale: "",
    multiplier: 1,
    mapbox: ["get", "n_loans"],
    display: "# of loans",
    js: (v) => v.n_loans,
    description:
      "The number of PPP loan applications submitted by food service businesses in a particular ZIP Code.",
  },
  ncompanies: {
    breaks: [0, 5, 20, 40, 60, 90, 400],
    scale: "",
    multiplier: 1,
    mapbox: ["get", "n_restaurants"],
    display: "# of restaurants",
    js: (v) => v.n_restaurants,
    description:
      "The number of food service businesses in a particular ZIP Code, according to 2017 US Census data.",
  },
  population: {
    breaks: [0, 4000, 15000, 26000, 35000, 47000, 106000],
    mapbox: ["get", "tot_pop"],
    scale: " people",
    multiplier: 1,
    display: "Population",
    color: (v) => d3.interpolateCividis(((v - 0.3) * 1) / 0.8),
    js: (v) => v.tot_pop,
  },
  density: {
    breaks: [0, 30e-6, 400e-6, 1200e-6, 3000e-6, 6000e-6, 20000e-6],
    mapbox: ["/", ["get", "tot_pop"], ["get", "aland10"]],
    scale: " people per square km",
    multiplier: 1e6,
    display: "Population density",
    color: (v) => d3.interpolateCividis(((v - 0.3) * 1) / 0.8),
    js: (v) => v.tot_pop / v.aland10,
  },
  pct_white: {
    breaks: [0, 0.2, 0.4, 0.5, 0.65, 0.8, 1].map((v) => v * 100),
    mapbox: ["get", "pct_white_alone"],
    multiplier: 1,
    scale: "%",
    display: "% White",
    color: (v) => d3.interpolateCividis(0.9 - ((v - 0.3) * 1) / 0.8),
    js: (v) => v.pct_white_alone,
  },
  pct_native: {
    display: "% Native American",
    scale: "%",
    breaks: [0, 0.5e-2, 1e-2, 2e-2, 5e-2, 10e-2, 32e-2].map((v) => v * 100),
    multiplier: 1,
    mapbox: ["get", "pct_amind"],
    color: (v) => d3.interpolateCividis(0.9 - ((v - 0.3) * 1) / 0.8),
    js: (v) => v.pct_amind,
  },
};

export const interactiveVariables = [
  ["PPP data", "Demographic data"],
  ["pct_with_loan", "loan_cnt", "ncompanies", "loan_total", "loan_per_company"],
  [
    "density",
    "pct_white",
    "hispanic_pct",
    "black_pct",
    "black_and_hispanic",
    "asian_pct",
    "pct_native",
    "med_income",
  ],
];
